// University of Idaho: public college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - The U.S. Department of Education's IPEDS Admissions survey for Fall
//    2024, first-time degree-seeking students (College Navigator, unit
//    142285, with the counts from the ADM2024 complete data file):
//    applicants, admits and enrollees. Departure: U of I posts only its
//    Common Data Set 2023-2024 (the "latest Common Data Set report" its
//    Institutional Effectiveness page links; earlier years only by request,
//    no 2024-2025 or 2025-2026 edition), so the newer IPEDS Fall 2024 counts
//    are used, with no residency split.
//  - That CDS 2023-2024: C14 (no closing date), C16 (rolling notification),
//    C21/C22 (no early decision or early action plan), which the Dates and
//    Deadlines page still matches. Its C8 (SAT and ACT "Recommended" for
//    Fall 2025) is two cycles old and no longer matches the admissions
//    page, so it is not used.
//  - U of I Admissions: First-year students (the admission requirements for
//    the "summer (May) 2027 start term and beyond", which cover fall 2027
//    entry, and the spring 2027 requirements they replace) and Admissions
//    Dates and Deadlines ("Fall 2027 Application is now open", rolling
//    admission). No page publishes an application deadline or decision
//    dates.
//  - Office of Student Financial Aid Services, Cost of attendance: the
//    2026-27 undergraduate budget, in-state and out-of-state. The page says
//    the State Board of Education finalizes the amounts each late spring.
//  - College Scorecard API, school id 142285 (net price by family income,
//    in-state).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=142285#admsns',
        read: '2026-10-01',
    },
    cds: {
        label: 'University of Idaho Common Data Set 2023-2024',
        url: 'https://www.uidaho.edu/leadership/provost/institutional-effectiveness',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'University of Idaho Admissions, First-year students',
        url: 'https://www.uidaho.edu/admissions-apply/first-year-students',
        read: '2026-10-01',
    },
    dates: {
        label: 'University of Idaho Admissions, Dates and Deadlines',
        url: 'https://www.uidaho.edu/admissions-apply/dates-deadlines',
        read: '2026-10-01',
    },
    coa: {
        label: 'University of Idaho Financial Aid, Cost of attendance 2026-27',
        url: 'https://www.uidaho.edu/financial-aid/cost-of-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?142285-University-of-Idaho',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-idaho',
    kbId: 'university_of_idaho',
    name: 'University of Idaho',
    shortName: 'U of I',
    aliases: ['UIdaho', 'U of I'],
    location: 'Moscow, ID',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (U of I brand guidelines); decoration only.
    branding: {
        primary: '#F1B300',
        secondary: '#808080',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_idaho_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 13443,
        admitted: 10154,
        enrolled: 2025,
        source: 'ipeds',
        where: 'first-time, degree-seeking first-year students (U of I posts no Common Data Set newer than 2023-2024)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'U of I posts no Common Data Set newer than 2023-2024, so these counts come from the Department of Education\'s IPEDS survey for Fall 2024, which gives one first-year rate for the whole university and none by college or major.',
        source: 'ipeds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'U of I has no early decision or early action plan. It has no application closing date and sends decisions on a rolling basis.',
        source: 'cds',
        where: 'sections C14, C16, C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Admission', value: 'Rolling' },
        rows: [
            { plan: 'Rolling admission', deadline: 'No closing date', decision: null, binding: false },
        ],
        note: 'The fall 2027 application opened in August 2026. U of I admits students and awards scholarships and other financial aid on a rolling basis as applications arrive. Accept your financial aid awards and send final transcripts by June 1, 2027; fall classes begin August 23, 2027. U of I does not publish decision dates.',
        source: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'For the summer 2027 term and beyond, which includes fall 2027, U of I asks for no ACT or SAT score from applicants at accredited high schools. A cumulative GPA of 2.6 or higher, or for Idaho residents a 3 or higher on both the ISAT math and literacy tests, brings automatic admission; a GPA of 2.30 to 2.59 brings admission through the Vandal Gateway Program, and below 2.30 you may appeal to the Admissions Committee. Applicants with a GED, from home school or from a non-accredited high school send ACT or SAT scores and go to the Admissions Committee.',
        source: 'firstYear',
    },

    currentChanges: [
        {
            topic: 'Admission requirements',
            line: 'Starting with the summer 2027 term, U of I admits on GPA alone and automatic admission starts at a 2.6 GPA. For spring 2027 it starts at 3.00, and applicants with a 2.60 to 2.99 GPA must send ACT or SAT scores or join the Vandal Gateway Program.',
            source: 'firstYear',
        },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [9824, 29300] },
            { item: 'Living expenses', amounts: [12995, 12995] },
            { item: 'Books and supplies', amounts: [1260, 1260] },
            { item: 'Transportation', amounts: [2874, 2874] },
            { item: 'Miscellaneous and personal', amounts: [3838, 3838] },
        ],
        totals: [30791, 50267],
        note: 'For a full-time undergraduate, fall and spring. WUE and Invitation to Idaho recipients pay $13,538 in tuition and fees, for a $34,505 total. College of Art and Architecture students pay a $725 program fee each semester. The State Board of Education sets the final amounts each late spring.',
        source: 'coa',
        netPrice: {
            average: 15321,
            byIncome: [
                { income: '$0 to $30,000', amount: 13085 },
                { income: '$30,001 to $48,000', amount: 11045 },
                { income: '$48,001 to $75,000', amount: 11914 },
                { income: '$75,001 to $110,000', amount: 16420 },
                { income: 'Over $110,000', amount: 19498 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from U of I\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
